import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Badge } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  ShoppingCartOutlined,
  TagsOutlined,
  FileOutlined,
  UserOutlined,
  LogoutOutlined,
  AccountBookOutlined,
  ScanOutlined,
  AuditOutlined,
  TeamOutlined,
  ExceptionOutlined,
  GlobalOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import Logo from '../Logo';
import Version from '../Version';
import { logout } from '../../utils/auth';

const { Content, Sider } = Layout;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userStr = localStorage.getItem('user');
  const user = JSON.parse(userStr);
  const [exceptionCount, setExceptionCount] = useState(0);
  const [unprocessedAirwayBillCount, setUnprocessedAirwayBillCount] = useState(0);

  // Fetch unprocessed airway bill count
  useEffect(() => {
    const fetchUnprocessedCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/orders/airway-bills/count/?is_processed=false', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setUnprocessedAirwayBillCount(data.count);
      } catch (error) {
        console.error('Failed to fetch unprocessed airway bill count:', error);
      }
    };

    fetchUnprocessedCount();
    // Refresh count every minute
    const interval = setInterval(fetchUnprocessedCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch exception count
  useEffect(() => {
    const fetchExceptionCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/orders/count?status=异常件', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        setExceptionCount(data.count);
      } catch (error) {
        console.error('Failed to fetch exception count:', error);
      }
    };

    fetchExceptionCount();
    // Refresh count every minute
    const interval = setInterval(fetchExceptionCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define menu items based on user role
  let menuItems = [];
  
  if (user && user.role === 'BOSS') {
    // Full menu for boss
    menuItems = [
      {
        key: 'airwaybills',
        icon: <FileOutlined />,
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            空运单管理
            {unprocessedAirwayBillCount > 0 && (
              <Badge 
                count={unprocessedAirwayBillCount}
                style={{ 
                  marginLeft: 8,
                  backgroundColor: '#ff4d4f',
                  boxShadow: 'none'
                }}
              />
            )}
          </span>
        ),
        onClick: () => navigate('/admin/airwaybills')
      },
      {
        key: 'batches',
        icon: <TagsOutlined />,
        label: '批次管理',
        onClick: () => navigate('/admin/batches')
      },
      {
        key: 'orders',
        icon: <ShoppingCartOutlined />,
        label: '订单管理',
        onClick: () => navigate('/admin/orders')
      },
      {
        key: 'exception',
        icon: <ExceptionOutlined />,
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            异常件
            {exceptionCount > 0 && (
              <Badge 
                count={exceptionCount}
                style={{ 
                  marginLeft: 8,
                  backgroundColor: '#ff4d4f',
                  boxShadow: 'none'
                }}
              />
            )}
          </span>
        ),
        onClick: () => navigate('/admin/exception')
      },
      {
        key: 'customers',
        icon: <UserOutlined />,
        label: '客户管理',
        onClick: () => navigate('/admin/customers')
      },
      {
        key: 'staff',
        icon: <TeamOutlined />,
        label: '员工管理',
        onClick: () => navigate('/admin/staff')
      },
      {
        key: 'bill-verification',
        icon: <AuditOutlined />,
        label: '账单核算',
        onClick: () => navigate('/admin/bill-verification')
      },
      {
        key: 'finance',
        icon: <BarChartOutlined />,
        label: '统计报表',
        onClick: () => navigate('/admin/finance')
      },
      {
        key: 'scan-print',
        icon: <ScanOutlined />,
        label: '扫描打印',
        onClick: () => navigate('/admin/scan-print')
      },
      {
        key: 'tracking',
        icon: <GlobalOutlined />,
        label: '包裹跟踪',
        onClick: () => navigate('/admin/tracking')
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: handleLogout,
      }
    ];
  } else if (user && user.role === 'MANAGER') {
    // Staff menu plus customer management for manager
    menuItems = [
      {
        key: 'airwaybills',
        icon: <FileOutlined />,
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            空运单管理
            {unprocessedAirwayBillCount > 0 && (
              <Badge 
                count={unprocessedAirwayBillCount}
                style={{ 
                  marginLeft: 8,
                  backgroundColor: '#ff4d4f',
                  boxShadow: 'none'
                }}
              />
            )}
          </span>
        ),
        onClick: () => navigate('/admin/airwaybills')
      },
      {
        key: 'batches',
        icon: <TagsOutlined />,
        label: '批次管理',
        onClick: () => navigate('/admin/batches')
      },
      {
        key: 'orders',
        icon: <ShoppingCartOutlined />,
        label: '订单管理',
        onClick: () => navigate('/admin/orders')
      },
      {
        key: 'exception',
        icon: <ExceptionOutlined />,
        label: (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            异常件
            {exceptionCount > 0 && (
              <Badge 
                count={exceptionCount}
                style={{ 
                  marginLeft: 8,
                  backgroundColor: '#ff4d4f',
                  boxShadow: 'none'
                }}
              />
            )}
          </span>
        ),
        onClick: () => navigate('/admin/exception')
      },
      {
        key: 'customers',
        icon: <UserOutlined />,
        label: '客户管理',
        onClick: () => navigate('/admin/customers')
      },
      {
        key: 'staff',
        icon: <TeamOutlined />,
        label: '员工管理',
        onClick: () => navigate('/admin/staff')
      },
      {
        key: 'bill-verification',
        icon: <AuditOutlined />,
        label: '账单核算',
        onClick: () => navigate('/admin/bill-verification')
      },
      {
        key: 'scan-print',
        icon: <ScanOutlined />,
        label: '扫描打印',
        onClick: () => navigate('/admin/scan-print')
      },
      {
        key: 'tracking',
        icon: <GlobalOutlined />,
        label: '包裹跟踪',
        onClick: () => navigate('/admin/tracking')
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: handleLogout,
      }
    ];
  }

  // Get the current selected key from the path
  const selectedKey = location.pathname.split('/')[2] || 'orders';

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Sider 
        width={200} 
        theme="light"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Logo />
        </div>
        <Version />
        <div style={{ marginLeft: '30px', marginBottom: '10px', textAlign: 'left' }}>
          <Typography.Text >欢迎您，{user?.username}</Typography.Text>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ flex: 1, borderRight: 0 }}
          items={menuItems}
        />
      </Sider>
      <Layout style={{ marginLeft: 200, background: '#fff', minHeight: '100vh' }}>
        <Content style={{ margin: '10px' }}>
          <div style={{ padding: '2px 10px', minHeight: 280 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
